import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, Outlet } from "@tanstack/react-router"
import { useState, type ReactNode } from "react"
import { ApiError, get, post } from "../lib/api"
import type { Me } from "../lib/types"
import { errorText, Loading } from "./ui"

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: () => get<Me>("/me"),
    staleTime: 60_000,
  })
}

function SignIn({ notAdmin, note }: { notAdmin?: string; note?: string }) {
  const qc = useQueryClient()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  return (
    <main className="signin">
      <form
        className="card"
        onSubmit={async (e) => {
          e.preventDefault()
          setBusy(true)
          setErr(null)
          try {
            await post("/auth/sign-in/email", { email, password })
            await qc.invalidateQueries({ queryKey: ["me"] })
          } catch (e2) {
            setErr(
              e2 instanceof ApiError && e2.status === 401
                ? "Wrong email or password"
                : errorText(e2),
            )
          } finally {
            setBusy(false)
          }
        }}
      >
        <div className="row">
          <Mark />
          <div>
            <h1 style={{ fontSize: 24 }}>Admin console</h1>
            <span className="muted small">Sign in with an admin account</span>
          </div>
        </div>
        {notAdmin && (
          <div className="note-box">
            {notAdmin} {note ?? "is signed in but isn't an admin."}{" "}
            <button
              type="button"
              className="btn btn-sm"
              onClick={async () => {
                await post("/auth/sign-out")
                await qc.invalidateQueries({ queryKey: ["me"] })
              }}
            >
              Sign out
            </button>
          </div>
        )}
        <label className="field">
          Email
          <input
            className="input"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>
        <label className="field">
          Password
          <input
            className="input"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        {err && <div className="err-box">{err}</div>}
        <button className="btn btn-primary btn-lg" disabled={busy}>
          Sign in
        </button>
      </form>
    </main>
  )
}

function Mark() {
  return (
    <span className="brand-mark" aria-hidden="true">
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#fff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7l4 3-1.5 4.5h-5L8 10z" />
      </svg>
    </span>
  )
}

interface NavCounts {
  counts: { openIssues: number; needsRescore: number }
  live: unknown[]
}

const NAV: {
  to: string
  label: string
  count?: (d: NavCounts) => number
  section?: string
}[] = [
  { to: "/", label: "Dashboard" },
  { to: "/slate", label: "Weekly slate", section: "Matchdays" },
  { to: "/matches", label: "Matches", count: (d) => d.live.length },
  { to: "/issues", label: "Data issues", count: (d) => d.counts.openIssues },
  { to: "/articles", label: "Articles", section: "Content" },
  { to: "/competitions", label: "Competitions", section: "Catalogue" },
  { to: "/teams", label: "Teams and squads" },
  { to: "/players", label: "Players" },
  { to: "/users", label: "Users", section: "People" },
  { to: "/leagues", label: "Private leagues" },
  { to: "/boards", label: "Boards and standings", section: "Game" },
  { to: "/settings", label: "Settings" },
  { to: "/audit", label: "Audit log" },
]

export function Shell() {
  const me = useMe()
  const qc = useQueryClient()
  const counts = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => get<NavCounts & { round: unknown }>("/admin/dashboard"),
    enabled: me.data?.user?.role === "admin",
    refetchInterval: 30_000,
  })
  if (me.isLoading) return <Loading />
  if (!me.data?.user) return <SignIn />
  if (me.data.user.role !== "admin")
    return <SignIn notAdmin={me.data.user.email} />
  if (!me.data.user.emailVerified)
    return (
      <SignIn
        notAdmin={me.data.user.email}
        note="has the admin role but hasn't confirmed its email yet. Open the link in the confirmation email, then reload."
      />
    )
  const user = me.data.user
  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <div style={{ height: 35, width: "auto", position: "relative" }}>
            <img
              src="/logo-ww.png"
              alt="10predicts logo"
              style={{ height: "100%", width: "100%", objectFit: "contain" }}
            />
          </div>
        </div>
        <nav className="nav" aria-label="Admin">
          {NAV.map((n) => (
            <NavItem
              key={n.to}
              {...n}
              n={counts.data && n.count ? n.count(counts.data) : 0}
            />
          ))}
        </nav>
        <div className="side-foot">
          <span>{user.email}</span>
          <a
            href={import.meta.env.VITE_PLAYER_URL ?? "http://localhost:3000"}
            target="_blank"
            rel="noreferrer"
            style={{ color: "#c4cbc0" }}
          >
            Open the player site ↗
          </a>
          <button
            type="button"
            onClick={async () => {
              await post("/auth/sign-out")
              qc.clear()
              await qc.invalidateQueries({ queryKey: ["me"] })
            }}
          >
            Sign out
          </button>
        </div>
      </aside>
      <div className="main">
        <Outlet />
      </div>
    </div>
  )
}

function NavItem({
  to,
  label,
  n,
  section,
}: {
  to: string
  label: string
  n: number
  section?: string
}) {
  return (
    <>
      {section && <div className="nav-section">{section}</div>}
      <Link to={to} activeOptions={{ exact: to === "/" }}>
        {label}
        {n > 0 && <span className="count">{n}</span>}
      </Link>
    </>
  )
}

export function Topbar({
  title,
  sub,
  actions,
  children,
}: {
  title: ReactNode
  sub?: ReactNode
  actions?: ReactNode
  children?: ReactNode
}) {
  return (
    <header className="topbar">
      <div>
        <h1>{title}</h1>
        {sub && <div className="sub">{sub}</div>}
      </div>
      {children}
      {actions && <div className="actions">{actions}</div>}
    </header>
  )
}
